import React, { useEffect, useMemo, useState } from 'react';
import { Button, Dropdown, Popconfirm } from '@robinpowered/ui-kit';
import type { MenuProps } from '@robinpowered/ui-kit';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faEdit,
  faFlag,
  faLink,
  faTrash,
  faUnlock,
} from '@awesome.me/kit-cf858715fd/icons/classic/solid';
import MoreHorizontalSolid from '@robinpowered/ui-kit-icons/MoreHorizontalSolid';
import { ReservationMenuAction } from '../../utils/getReservationActions';
import { useReportProfileSidebarPopup } from '../../../ProfileSidebarPopupContext';
import {
  overlayDropdownStyles,
  overlayPopconfirmStyles,
} from '../../../styles';

type ReservationActionsMenuProps = {
  actions: ReservationMenuAction[];
  onAction: (action: ReservationMenuAction) => void;
};

export const ReservationActionsMenu = ({
  actions,
  onAction,
}: ReservationActionsMenuProps): JSX.Element | null => {
  const { t } = useTranslation('peopleProfileSidebar', {
    keyPrefix: 'bookings',
  });
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isUnassignConfirmOpen, setIsUnassignConfirmOpen] = useState(false);

  // The popups portal to document.body so they can extend past the drawer's
  // width; report their open state so the sidebar's outside-mousedown
  // dismissal leaves them (and itself) alone while they're in use. Balanced
  // open/close reporting (the close also fires on unmount) so the sidebar can
  // track concurrently open popups with a simple counter.
  const reportPopupOpen = useReportProfileSidebarPopup();
  const isPopupOpen = isDropdownOpen || isUnassignConfirmOpen;
  useEffect(() => {
    if (!isPopupOpen) {
      return;
    }
    reportPopupOpen(true);
    return () => reportPopupOpen(false);
  }, [isPopupOpen, reportPopupOpen]);

  const items: MenuProps['items'] = useMemo(
    () =>
      actions.map((action) => {
        if (action === 'copy') {
          return {
            key: action,
            icon: <FontAwesomeIcon icon={faLink} />,
            label: t('menu.copyLink'),
          };
        }
        if (action === 'edit') {
          return {
            key: action,
            icon: <FontAwesomeIcon icon={faEdit} />,
            label: t('menu.edit'),
          };
        }
        if (action === 'reportIssue') {
          return {
            key: action,
            icon: <FontAwesomeIcon icon={faFlag} />,
            label: t('menu.reportIssue'),
          };
        }
        if (action === 'unassign') {
          return {
            key: action,
            danger: true,
            icon: <FontAwesomeIcon icon={faTrash} />,
            label: t('menu.unassign'),
          };
        }
        if (action === 'release') {
          return {
            key: action,
            icon: <FontAwesomeIcon icon={faUnlock} />,
            label: t('menu.release'),
          };
        }
        if (action === 'cancel') {
          return {
            key: action,
            danger: true,
            icon: <FontAwesomeIcon icon={faTrash} />,
            label: t('menu.cancel'),
          };
        }
        return null;
      }),
    [actions, t]
  );

  if (actions.length === 0) {
    return null;
  }

  const stopMouse: React.MouseEventHandler<HTMLElement> = (event) =>
    event.stopPropagation();
  const stopMaybeMouse = (event?: React.MouseEvent<HTMLElement>) => {
    event?.stopPropagation();
  };
  const stopActivationKeys: React.KeyboardEventHandler<HTMLElement> = (
    event
  ) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.stopPropagation();
    }
  };

  return (
    <Popconfirm
      open={isUnassignConfirmOpen}
      styles={overlayPopconfirmStyles}
      title={t('unassignConfirmTitle')}
      description={t('unassignConfirmDescription')}
      okText={t('unassignConfirmOk')}
      cancelText={t('unassignConfirmCancel')}
      okButtonProps={{ danger: true }}
      onOpenChange={(open) => {
        if (!open) {
          setIsUnassignConfirmOpen(false);
        }
      }}
      onCancel={(event) => {
        stopMaybeMouse(event);
        setIsUnassignConfirmOpen(false);
      }}
      onConfirm={(event) => {
        stopMaybeMouse(event);
        setIsUnassignConfirmOpen(false);
        onAction('unassign');
      }}
    >
      <Dropdown
        trigger={['click']}
        open={isDropdownOpen}
        onOpenChange={setIsDropdownOpen}
        styles={overlayDropdownStyles}
        menu={{
          items,
          onClick: (info) => {
            info.domEvent.stopPropagation();
            setIsDropdownOpen(false);
            if (info.key === 'unassign') {
              setIsUnassignConfirmOpen(true);
              return;
            }
            onAction(info.key as ReservationMenuAction);
          },
        }}
      >
        <Button
          type="text"
          size="small"
          aria-label={t('actionsMenuLabel')}
          icon={<MoreHorizontalSolid />}
          onClick={stopMouse}
          onKeyDown={stopActivationKeys}
        />
      </Dropdown>
    </Popconfirm>
  );
};
